import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Bell, Bot, Eye, History } from 'lucide-react'
import type { ReactNode } from 'react'
import { copy } from '../../copy/investor-copy'
import { PrivateTag, RelCard } from './rel-card'
import type { CardActions, CardVariant, Relationship } from './types'

function Bullet({ icon, children }: { icon: ReactNode; children: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-elevated text-muted-foreground [&_svg]:size-3.5">
        {icon}
      </span>
      <span className="mt-0.5 text-sm leading-snug text-foreground/85">{children}</span>
    </li>
  )
}

/**
 * The two cards of a startup the investor does not follow: why to follow, and the AI's invitation.
 * Only beside the profile on a wide screen (the prototype's `sf-side-only`): the page provides `@container`.
 */
export function NoFollowCards({
  data,
  variant,
  actions,
  part,
}: {
  data: Relationship
  variant: CardVariant
  actions: CardActions
  /** The follow card goes at the top of the panel; the invitation to connect the AI goes below. */
  part: 'follow' | 'ai'
}) {
  const startup = data.startup.name
  const { used, limit, plan } = data.allowance
  const wrapper = variant === 'page' ? 'hidden space-y-4 @min-[900px]:block' : 'space-y-4'
  if (part === 'ai') {
    return data.assessment === null ? (
      <div className={wrapper}>
        <RelCard title={copy('ai.title')} right={<PrivateTag label={copy('ai.private')} />}>
          <p className="mt-2 text-sm leading-relaxed text-foreground/85">
            {copy('ai.empty', { startup })}
          </p>
          <div className="mt-3">
            <SecondaryButton size="sm" icon={<Bot />} onClick={actions.onConnectAi}>
              {copy('ai.connect')}
            </SecondaryButton>
          </div>
        </RelCard>
      </div>
    ) : null
  }
  return (
    <div className={wrapper}>
      <RelCard title={copy('card.nofollow.title')}>
        <ul className="mt-3 space-y-3">
          <Bullet icon={<Bell />}>{copy('card.nofollow.line1', { startup })}</Bullet>
          <Bullet icon={<History />}>{copy('card.nofollow.line2')}</Bullet>
          <Bullet icon={<Eye />}>{copy('card.nofollow.line3')}</Bullet>
        </ul>
        <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
          {plan === 'FREE'
            ? copy('card.allowance.free', { used, limit })
            : copy('card.allowance.paid', { used, limit })}
        </p>
      </RelCard>
    </div>
  )
}
